import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import {
  getTemplateAssignmentsRoutine,
  createTemplateAssignmentRoutine,
  deleteTemplateAssignmentRoutine
} from '../ducks/actions'
import {
  selectTemplateAssignments,
  selectTemplateAssignmentsLoading
} from '../ducks/selectors'
import { fetchUserBps } from 'services/BpService'
import { fetchBpUnits } from 'services/unitsService'
import SearchableSelect from 'components/SearchableSelect'

const TemplateAssignments = ({ templateId }) => {
  const dispatch = useDispatch()
  const assignments = useSelector(selectTemplateAssignments)
  const loading = useSelector(selectTemplateAssignmentsLoading)

  const [showForm, setShowForm] = useState(false)
  const [bps, setBps] = useState([])
  const [bpsLoading, setBpsLoading] = useState(false)
  const [units, setUnits] = useState([])
  const [unitsLoading, setUnitsLoading] = useState(false)
  const [selectedBpId, setSelectedBpId] = useState('')
  const [selectedUnitIds, setSelectedUnitIds] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (templateId) {
      dispatch(getTemplateAssignmentsRoutine(templateId))
    }
  }, [dispatch, templateId])

  useEffect(() => {
    if (!showForm) return
    const loadBps = async () => {
      setBpsLoading(true)
      try {
        const result = await fetchUserBps({ page: 1, sort: 'name', filters: '&filter[task_management_enabled]=1&limit=250' })
        const entries = result?.data?.entries || result?.data?.data || []
        setBps(entries)
      } catch (error) {
        console.error('Failed to load BPs:', error)
      } finally {
        setBpsLoading(false)
      }
    }
    loadBps()
  }, [showForm])

  useEffect(() => {
    if (!selectedBpId) {
      setUnits([])
      return
    }
    const loadUnits = async () => {
      setUnitsLoading(true)
      try {
        const result = await fetchBpUnits({ bpId: selectedBpId })
        const entries = result?.data?.data || result?.data || []
        setUnits(Array.isArray(entries) ? entries : [])
      } catch {
        setUnits([])
      } finally {
        setUnitsLoading(false)
      }
    }
    loadUnits()
    setSelectedUnitIds([])
  }, [selectedBpId])

  const handleCreate = () => {
    if (!selectedBpId) return
    setSaving(true)
    dispatch(createTemplateAssignmentRoutine({
      templateId,
      buildingPassportId: selectedBpId,
      unitIds: selectedUnitIds.length > 0 ? selectedUnitIds : undefined,
      onSuccess: () => {
        setSaving(false)
        setShowForm(false)
        setSelectedBpId('')
        setSelectedUnitIds([])
      },
      onError: () => {
        setSaving(false)
      },
    }))
  }

  const handleDelete = (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return
    dispatch(deleteTemplateAssignmentRoutine({ templateId, assignmentId }))
  }

  const existingBpIds = assignments.map(a => a.buildingPassportId)
  const availableBps = bps.filter(bp => !existingBpIds.includes(bp.id))

  return (
    <Wrapper>
      <Header>
        <Title>Building Passport Assignments</Title>
        {!showForm && (
          <AddBtn onClick={() => setShowForm(true)}>
            <AddIcon style={{ fontSize: 16 }} />
            Add Assignment
          </AddBtn>
        )}
      </Header>

      {showForm && (
        <FormCard>
          <FormGroup>
            <Label>Building Passport</Label>
            <SearchableSelect
              value={selectedBpId || null}
              onChange={v => setSelectedBpId(v)}
              options={availableBps.map(bp => ({ value: bp.id, label: bp.name }))}
              placeholder={bpsLoading ? 'Loading...' : 'Select a Building Passport'}
              searchPlaceholder='Search Building Passports…'
              ariaLabel='Building Passport'
            />
          </FormGroup>

          {selectedBpId && units.length > 0 && (
            <FormGroup>
              <Label>Units (optional)</Label>
              <UnitList>
                {units.map(unit => (
                  <UnitRow key={unit.id}>
                    <input
                      type="checkbox"
                      checked={selectedUnitIds.includes(unit.id)}
                      onChange={() => {
                        setSelectedUnitIds(prev =>
                          prev.includes(unit.id)
                            ? prev.filter(id => id !== unit.id)
                            : [...prev, unit.id]
                        )
                      }}
                    />
                    <span>{unit.name}</span>
                  </UnitRow>
                ))}
              </UnitList>
            </FormGroup>
          )}

          <FormActions>
            <CancelBtn onClick={() => { setShowForm(false); setSelectedBpId(''); setSelectedUnitIds([]) }}>
              Cancel
            </CancelBtn>
            <SaveBtn onClick={handleCreate} disabled={!selectedBpId || saving}>
              {saving ? 'Saving...' : 'Add'}
            </SaveBtn>
          </FormActions>
        </FormCard>
      )}

      {loading ? (
        <EmptyText>Loading...</EmptyText>
      ) : assignments.length === 0 ? (
        <EmptyText>No assignments yet. Assign this template to Building Passports for auto-renewal.</EmptyText>
      ) : (
        <AssignmentList>
          {assignments.map(assignment => (
            <AssignmentRow key={assignment.id}>
              <AssignmentInfo>
                <AssignmentBuilding>
                  <ApartmentOutlinedIcon style={{ fontSize: 16 }} />
                  {assignment.building?.name || 'Unknown Building Passport'}
                </AssignmentBuilding>
                {assignment.units && assignment.units.length > 0 && (
                  <AssignmentUnits>
                    <MeetingRoomOutlinedIcon style={{ fontSize: 14 }} />
                    {assignment.units.map(u => u.name).join(', ')}
                  </AssignmentUnits>
                )}
                {assignment.nextDueAt && (
                  <NextDue>Next due: {assignment.nextDueAt}</NextDue>
                )}
              </AssignmentInfo>
              <DeleteBtn onClick={() => handleDelete(assignment.id)} title="Remove assignment">
                <DeleteOutlineIcon style={{ fontSize: 16 }} />
              </DeleteBtn>
            </AssignmentRow>
          ))}
        </AssignmentList>
      )}
    </Wrapper>
  )
}

export default TemplateAssignments

const Wrapper = styled.div`
  margin-top: 24px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  background: white;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const Title = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const FormCard = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.lightGrey}33;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  margin-bottom: 16px;
`

const FormGroup = styled.div`
  margin-bottom: 12px;
`

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const UnitList = styled.div`
  max-height: 160px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  background: white;
`

const UnitRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
  cursor: pointer;
`

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const CancelBtn = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  cursor: pointer;
`

const SaveBtn = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: ${({ theme }) => theme.colors.haiti};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const EmptyText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  text-align: center;
  padding: 12px 0;
  margin: 0;
`

const AssignmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AssignmentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.lightGrey}33;
`

const AssignmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const AssignmentBuilding = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    opacity: 0.6;
    flex-shrink: 0;
  }
`

const AssignmentUnits = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  margin-top: 4px;
  margin-left: 22px;

  svg {
    opacity: 0.6;
    flex-shrink: 0;
  }
`

const NextDue = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
  margin-left: 22px;
`

const DeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => `${theme.colors.error}15`};
    color: ${({ theme }) => theme.colors.error};
  }
`
